import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { ACT_AS_HEADER } from "../../utils/actAs";

/**
 * 支払明細書（payment statements）API。
 *
 * 用語の対応:
 * - UI/型名: PaymentStatement（支払明細書）
 * - BE モデル/URL: PaymentRequest / `payment-requests`（振込申請レコード = 明細書 1 件）
 * - レスポンスキー: `data.payment_requests`（BE 互換のためそのまま）
 *
 * - 本人一覧 : `GET /api/me/payment-requests`
 * - 管理者一覧: `GET /api/admin/payment-requests`（MASTERADMIN のみ）
 * - ステータス: `PATCH /api/admin/payment-requests/:id`（支払い済み⇔支払い待ち トグル）
 *
 * PDF は一覧が返す `pdf_url` を `downloadPaymentStatementPdf()` で取得する。
 * `pdf_url` が `null` の明細は PDF 未生成（DL 不可）。
 */

/** 管理者一覧で表示する申請者情報（銀行口座・法人区分つき）。 */
export interface PaymentStatementCreator {
  id: number;
  display_name: string;
  avatar_url: string | null;
  /** 銀行名（未登録は null / 空文字）。管理者一覧のみ返る。 */
  bank?: string | null;
  /** 支店名。管理者一覧のみ返る。 */
  bank_branch?: string | null;
  /** 支店番号（3桁）。管理者一覧のみ返る。 */
  bank_branch_number?: string | null;
  /** 口座種別の表示名（普通 / 当座 など）。管理者一覧のみ返る。 */
  account_type_display?: string;
  /** 口座番号。管理者一覧のみ返る。 */
  account_number?: string | null;
  /** 口座名義。管理者一覧のみ返る。 */
  account_name?: string | null;
  /**
   * 法人区分の実効値（true=法人 / false=個人）。
   * DB 未設定（null）の場合は BE が口座名義から自動判定した値を返す。
   */
  is_corporation?: boolean;
}

/**
 * 取消済みの `status_type` 値（BE `PaymentRequest.CANCELED_STATUS`）。
 * 運用コマンドで取り消された申請。振込対象外なので UI は「取消済み」と
 * 表示し、ステータス切替を出さない（BE も PATCH を 400 で拒否する）。
 */
export const PAYMENT_STATUS_CANCELED = 5;

/** 支払明細書 1 件。 */
export interface PaymentStatement {
  id: number;
  payment_code: string;
  /**
   * 金額（円・税込・整数）。BE が税込に揃えた値を文字列で返す（例 `"11000"`）。
   * 新4バケット決済は税抜保存のため BE 側で消費税を加算済み、旧環境は税込のまま。
   */
  amount: string;
  /** ステータス種別（3=支払い済み / 5=取消済み。他は支払い待ち扱い）。 */
  status_type: number;
  /** 支払い済みか（`status_type === 3`）。UI はこのフラグを使う。 */
  is_paid: boolean;
  /** 支払日（支払い済みのときのみ ISO 文字列、未払いは null）。 */
  settlement_date: string | null;
  /** 申請日時（ISO 文字列）。 */
  created: string;
  /** 申請日（= created の別名）。 */
  application_date: string;
  /** 生成済み PDF の絶対 URL（未生成は null）。 */
  pdf_url: string | null;
  /** 申請者（管理者一覧でのみ意味を持つ）。 */
  creator: PaymentStatementCreator | null;
  /**
   * 振込額（円・整数の文字列）。管理者一覧のみ返る。
   * 法人は対価（amount）そのまま、個人は源泉徴収控除後の金額。
   */
  transfer_amount?: string;
}

export interface PaymentStatementListData {
  payment_requests: PaymentStatement[];
  count: number;
  limit: number;
  offset: number;
  has_next: boolean;
  /** データが存在する年の降順リスト（年度フィルターの選択肢）。 */
  available_years: number[];
  /** 現在絞り込んでいる年（"all" は全期間）。 */
  selected_year: number | "all";
}

export interface PaymentStatementListResponse {
  status: string;
  data: PaymentStatementListData;
}

export interface PaymentStatementMutationResponse {
  status: string;
  data: { payment_request: PaymentStatement };
}

export interface PaymentStatementListParams {
  /** 年度（数値）または "all"。未指定時は BE 側で当年扱い。 */
  year?: number | "all";
  offset?: number;
  limit?: number;
}

const buildListUrl = (
  base: string,
  { year, offset, limit }: PaymentStatementListParams = {},
): string => {
  const params = new URLSearchParams();
  if (year !== undefined) params.set("year", String(year));
  if (offset !== undefined) params.set("offset", String(offset));
  if (limit !== undefined) params.set("limit", String(limit));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

export const paymentStatementsApi = createApi({
  reducerPath: "paymentStatementsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["PaymentStatements"],
  endpoints: (builder) => ({
    getMyPaymentStatements: builder.query<
      PaymentStatementListResponse,
      PaymentStatementListParams
    >({
      query: (params) => buildListUrl("/api/me/payment-requests", params),
      providesTags: [{ type: "PaymentStatements", id: "ME_LIST" }],
      keepUnusedDataFor: 30,
    }),
    getAdminPaymentStatements: builder.query<
      PaymentStatementListResponse,
      PaymentStatementListParams
    >({
      query: (params) => buildListUrl("/api/admin/payment-requests", params),
      providesTags: [{ type: "PaymentStatements", id: "ADMIN_LIST" }],
      keepUnusedDataFor: 30,
    }),
    updatePaymentStatementStatus: builder.mutation<
      PaymentStatementMutationResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/api/admin/payment-requests/${id}`,
        method: "PATCH",
      }),
      // ステータス切替後、管理者一覧を再取得して表示を整合させる。
      invalidatesTags: [{ type: "PaymentStatements", id: "ADMIN_LIST" }],
    }),
    /**
     * 法人区分の切り替え（ユーザー単位）。
     * 対象ユーザー本人としての `PATCH /api/me`（act-as）で
     * `AuthUser.is_corporation` を更新する（MASTERADMIN の代理操作）。
     * 振込額（transfer_amount）が変わるため管理者一覧を再取得する。
     */
    updateCreatorCorporation: builder.mutation<
      { status: string },
      { userId: number; isCorporation: boolean }
    >({
      query: ({ userId, isCorporation }) => ({
        url: "/api/me",
        method: "PATCH",
        headers: { [ACT_AS_HEADER]: String(userId) },
        body: { is_corporation: isCorporation },
      }),
      invalidatesTags: [{ type: "PaymentStatements", id: "ADMIN_LIST" }],
    }),
  }),
});

export const {
  useGetMyPaymentStatementsQuery,
  useGetAdminPaymentStatementsQuery,
  useUpdatePaymentStatementStatusMutation,
  useUpdateCreatorCorporationMutation,
} = paymentStatementsApi;
